<template>
  <v-card :height="285" flat>
    <v-skeleton-loader transition="fade-transition" type="article" v-if="loaderMetric" />
    <v-hover v-slot:default="{hover}" open-delay="200" v-else>
      <v-card-text>

        <!-- draggable handler -->
        <v-icon v-if="draggable && hover" class="vue-draggable-handle vue-draggable-handle--icon" color="secondary" size="30">mdi-drag-horizontal</v-icon>
        <!-- end draggable handler -->

        <!-- metric header -->
        <v-row align="center" justify="space-between" no-gutters>
          <v-col cols="9">
            <span class="d-inline-block body-2 text--secondary font-weight-medium text-truncate" style="max-width:100%">{{$t(`metrics.${metric}`)}}</span>
          </v-col>
          <v-col class="text-right" v-if="hover">
            <v-tooltip max-width="280px" transition="scale-transition" top>
              <template v-slot:activator="{on, attrs}">
                <v-icon class="mt-n1" v-bind="attrs" v-on="on" small color="secondary">mdi-information</v-icon>
              </template>
              <span>{{(config.description || 'Sin descripción')}}</span>
            </v-tooltip>
            <v-btn class="ml-2 mt-n1" v-if="removable" @click="$emit('destroy', true)" icon x-small><v-icon color="secondary" size="20">mdi-delete-outline</v-icon></v-btn>
          </v-col>
        </v-row>
        <!-- end metric header -->

        <!-- metric representation -->
        <v-row align="center" no-gutters>
          <v-col class="my-2" v-if="config.type.graph.class === 'line'">
            <chart-line
              class="d-block"
              axe-type-y="number"
              :background-color="config.type.graph.background_color"
              :colors="config.type.graph.colors"
              :chart-data="dataset"
              :chart-id="settings.i || `${metric}_graph`"
              :custom-labels="[$t(`metrics.${metric}`)]"
              :date-format="dateFormat"
              :date-format-axe="dateFormatAxe"
              :height="220"
              :number-rounded="true"
              :show-axe-x="false"
              :show-draw-border-grid-lines-axe-y="true"
              :show-grid-lines-axe-y="true"
              :show-point="true"
              style="position:relative;"
            />
          </v-col>
          <v-col class="mb-5" v-else-if="config.type.graph.class === 'barStacked'">
            <chart-bar-stacked
              class="d-block mt-4 chartjs-final-dataset-as-compute"
              axe-type-y="number"
              :chart-data="dataset"
              :chart-id="settings.i || `${metric}_graph`"
              :colors="config.type.graph.colors"
              :custom-labels="[$t(`metrics.${metric}`)]"
              :date-format="dateFormat"
              :date-format-axe="dateFormatAxe"
              :final-as-compute="config.type.graph.lastAsSum !== false"
              :height="220"
              :labels="config.type.graph.labels"
              :number-rounded="true"
              :show-draw-border-grid-lines-axe-y="true"
              style="position:relative"
            />
          </v-col>
          <v-col class="mb-5" v-else-if="config.type.graph.class === 'bar'">
            <chart-bar
              class="d-block mt-4"
              axe-type-y="number"
              :chart-data="dataset"
              :chart-id="settings.i || `${metric}_graph`"
              :colors="colors"
              :custom-labels="[$t(`metrics.${metric}`)]"
              :date-format="dateFormat"
              :date-format-axe="dateFormatAxe"
              :final-as-compute="config.type.graph.lastAsSum"
              :height="220"
              :labels="labels"
              :number-rounded="true"
              :show-draw-border-grid-lines-axe-y="true"
              style="position:relative"
            />
          </v-col>
        </v-row>
        <!-- end metric representation -->

        <!-- resize -->
        <v-btn v-if="resizable && hover" @click="$emit('resize', true)" class="vue-draggable-resize px-0" color="primary" height="60" x-small>
          <v-icon class="ml-0" dark x-small tab>{{ settings.w === 12 ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
        </v-btn>
        <!-- end resize -->

      </v-card-text>
    </v-hover>
  </v-card>
</template>
<script>
import moment from 'moment'

import ChartBar from '@/components/charts/ChartBar'
import ChartBarStacked from '@/components/charts/ChartBarStacked'
import ChartLine from '@/components/charts/ChartLine'

import MetricViewMixin from '@/mixins/MetricViewMixin'

export default {
  props: {
    draggable: {
      type: Boolean,
      required: false,
      default: () => (false)
    },
    interval: {
      type: String,
      required: false,
      default: () => ('monthly')
    },
    metric: {
      type: String,
      required: true
    },
    removable: {
      type: Boolean,
      required: false,
      default: () => (false)
    },
    resizable: {
      type: Boolean,
      required: false,
      default: () => (false)
    },
    settings: {
      type: Object,
      required: false,
      default: () => ({})
    },
    start: {
      type: String,
      required: false,
      default: () => (null)
    },
    end: {
      type: String,
      required: false,
      default: () => (null)
    },
    comparative: {
      type: String,
      required: false,
      default: () => ('this_v_last_mtd')
    },
    cache: {
      type: Boolean,
      required: false,
      default: () => (false)
    }
  },
  mixins: [
    MetricViewMixin
  ],
  components: {
    ChartBar,
    ChartBarStacked,
    ChartLine
  },
  computed: {
    // undefined lets each chart fall back to its own default, which is the
    // monthly format they already had hardcoded
    dateFormat () {
      return this.interval === 'daily' ? 'DD MMM YYYY' : undefined
    },
    dateFormatAxe () {
      return this.interval === 'daily' ? 'DD MMM' : undefined
    }
  },
  data: () => ({
    colors: ['#2962FF', '#5C6BC0', '#3F51B5', '#FFD600', '#FFEA00'],
    config: {},
    dailyStart: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    dailyEnd: moment().add(1, 'days').format('YYYY-MM-DD'),
    dataset: {},
    labels: []
  }),
  created () {
    this.retrieveMetric()
  },
  watch: {
    comparative () {
      this.retrieveMetric()
    },
    interval () {
      this.retrieveMetric()
    },
    metric () {
      this.retrieveMetric()
    }
  },
  methods: {
    async retrieveMetric () {
      this.loaderMetric = true

      const query = {
        interval: this.interval
      }

      if (this.start) {
        query.start = this.start
      }

      if (this.end) {
        query.end = this.end
      }

      if (this.comparative) {
        query.comparative = this.comparative
      }

      // default the daily interval to the last week, unless the caller
      // passed an explicit range through the start/end props
      if (this.interval === 'daily') {
        query.start = this.start || this.dailyStart
        query.end = this.end || this.dailyEnd
      }

      this.config = this.getMetric(this.metric)

      const data = {}
      const totals = {}
      const metrics = this.config.type.graph.metrics || [this.metric]

      for (const el of metrics) {
        query.group = el.group

        try {
          const r = await this.$store.dispatch('analytics/GET', {
            resource: `metrics/${el.metric || el}/graph`,
            query: query,
            cache: this.cache
          })

          if (el.sum) {
            if (Object.keys(totals).length === 0) {
              totals.datasets = r.data.datasets
              totals.labels = r.data.labels
            } else {
              totals.datasets = totals.datasets.concat(r.data.datasets)
            }
          } else {
            if (Object.keys(data).length === 0) {
              data.datasets = r.data.datasets
              data.labels = r.data.labels
            } else {
              data.datasets = data.datasets.concat(r.data.datasets)
            }
          }
        } catch (e) {
          console.error('Error retrieving metric graph data: ', e)
        }
      }

      // add the sum dataset to the data
      if (totals.datasets) {
        const sumDataset = totals.datasets[0].data.map((_, i) =>
          totals.datasets.reduce((sum, dataset) => sum + dataset.data[i], 0)
        )

        if (Object.keys(data).length === 0) {
          // add manually as the first dataset
          data.datasets = [{
            data: sumDataset,
            label: totals.datasets[0].label
          }]
          data.labels = totals.labels
        } else {
          // concat as n dataset
          data.datasets = data.datasets.concat({
            data: sumDataset,
            label: totals.datasets[0].label
          })
        }
      }

      // assign data to dataset
      this.dataset = data
      this.loaderMetric = false

      this.setColors()
      this.setLabels()
    },
    setColors () {
      const colors = this.config.type.graph.colors

      if (!colors) {
        return
      }

      // if colors exists and it's an array of strings, use it as is
      if (colors.every(item => typeof item === 'string')) {
        this.colors = this.config.type.graph.colors
        return
      }

      for (let i = 0; i < this.dataset.datasets.length; i++) {
        const dataset = this.dataset.datasets[i]
        const color = this.config.type.graph.colors.find((color) => {
          return color.label === dataset.label
        })

        this.colors[i] = color.color
      }
    },
    setLabels () {
      if (this.config.type.graph.labels) {
        this.labels = this.config.type.graph.labels
        return
      }

      this.labels = this.dataset.datasets.map((dataset) => {
        return this.$t(dataset.label)
      })
    }
  }
}
</script>